<template>
  <nav class="breadcrumb" aria-label="breadcrumb">
    <ol class="breadcrumb-list">
      <li
        v-for="(item, index) in breadcrumbItems"
        :key="item.path"
        class="breadcrumb-item"
        :class="{ 'active': index === breadcrumbItems.length - 1 }"
      >
        <router-link
          v-if="index < breadcrumbItems.length - 1"
          :to="item.query ? { path: item.path, query: item.query } : item.path"
          class="breadcrumb-link"
        >
          <component 
            v-if="item.icon" 
            :is="item.icon" 
            class="breadcrumb-icon"
          />
          {{ item.title }}
        </router-link>
        <span v-else class="breadcrumb-current">
          <component 
            v-if="item.icon" 
            :is="item.icon" 
            class="breadcrumb-icon"
          />
          {{ item.title }}
        </span>
        <span 
          v-if="index < breadcrumbItems.length - 1" 
          class="breadcrumb-separator"
          :aria-hidden="true"
        >
          {{ separator }}
        </span>
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { RouteLocationMatched } from 'vue-router'

interface BreadcrumbItem {
  title: string
  path: string
  icon?: any
  query?: Record<string, any>
}

interface Props {
  separator?: string
  showHome?: boolean
  homeTitle?: string
  customTitles?: Record<string, string>
  maxItems?: number
}

const props = withDefaults(defineProps<Props>(), {
  separator: '/',
  showHome: true,
  homeTitle: 'Início',
  customTitles: () => ({}),
  maxItems: 0
})

const route = useRoute()
const router = useRouter()

// Função para obter o título da rota
const getRouteTitle = (matched: RouteLocationMatched, routePath: string): string => {
  // Verificar se há título customizado via props
  if (props.customTitles[routePath]) {
    return props.customTitles[routePath]
  }
  
  // Verificar se há título nas meta tags da rota
  if (matched.meta?.title) {
    return matched.meta.title as string
  }
  
  // Verificar se há breadcrumbTitle específico
  if (matched.meta?.breadcrumbTitle) {
    return matched.meta.breadcrumbTitle as string
  }
  
  // Usar o nome da rota se disponível
  if (matched.name) {
    return formatRouteName(matched.name.toString())
  }
  
  // Usar o último segmento do path como fallback
  const segments = routePath.split('/').filter(Boolean)
  const lastSegment = segments[segments.length - 1]
  return formatRouteName(lastSegment || 'Página')
}

// Função para formatar nome da rota
const formatRouteName = (name: string): string => {
  return name
    .replace(/([A-Z])/g, ' $1') // Adicionar espaço antes de maiúsculas
    .replace(/^./, str => str.toUpperCase()) // Primeira letra maiúscula
    .replace(/\b\w/g, str => str.toUpperCase()) // Cada palavra com primeira letra maiúscula
}

// Computed que gera os itens do breadcrumb
const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
  const matched = route.matched.filter(record => record.name && record.path !== '/')
  const items: BreadcrumbItem[] = []
  
  // Adicionar item de início se habilitado
  if (props.showHome && route.path !== '/dashboard') {
    items.push({
      title: props.homeTitle,
      path: '/dashboard'
    })
  }
  
  // Gerar items baseados nas rotas matched
  for (let i = 0; i < matched.length; i++) {
    const record = matched[i]
    let currentPath = ''
    
    // Para rotas aninhadas, construir o path completo
    if (i === 0) {
      currentPath = record.path
    } else {
      // Para rotas filhas, usar o fullPath da rota atual se for a última
      if (i === matched.length - 1) {
        currentPath = route.path
      } else {
        // Para rotas pai, construir o path baseado na estrutura atual
        // mas parando no nível correto da hierarquia
        const routeSegments = route.path.split('/').filter(Boolean)
        const recordSegments = record.path.split('/').filter(Boolean)
        
        if (recordSegments.length <= routeSegments.length) {
          currentPath = '/' + routeSegments.slice(0, recordSegments.length).join('/')
        } else {
          currentPath = record.path
        }
      }
    }
    
    // Substituir parâmetros dinâmicos pelos valores reais (caso ainda tenham sobrado)
    if (route.params && currentPath.includes(':')) {
      for (const [key, value] of Object.entries(route.params)) {
        currentPath = currentPath.replace(`:${key}`, value as string)
      }
    }
    
    // Não duplicar o item atual se já existe
    if (!items.some(item => item.path === currentPath)) {
      items.push({
        title: getRouteTitle(record, currentPath),
        path: currentPath,
        icon: record.meta?.icon,
        // Preservar a query atual ao navegar para níveis superiores
        query: { ...route.query }
      })
    }
  }
  
  // Limitar número de items se especificado
  if (props.maxItems > 0 && items.length > props.maxItems) {
    const excess = items.length - props.maxItems
    items.splice(1, excess, {
      title: '...',
      path: '#'
    })
  }
  
  return items
})

// Reatividade para mudanças de rota
watch(() => route.path, () => {
  // Force recomputation when route changes
}, { immediate: true })
</script>

<style scoped>
.breadcrumb {
  font-size: 14px !important;
  margin-bottom: 0.2rem !important;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif !important;
}

.breadcrumb-list {
  display: flex !important;
  flex-wrap: wrap !important;
  align-items: center !important;
  list-style: none !important;
  padding: 0 !important;
  margin: 0 !important;
  gap: 0.25rem !important;
}

.breadcrumb-item {
  display: flex !important;
  align-items: center !important;
}

.breadcrumb-link {
  color: #6b7280 !important;
  text-decoration: none !important;
  transition: all 0.2s ease !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.375rem !important;
  padding: 0.25rem 0.5rem !important;
  border-radius: 0.375rem !important;
  font-weight: 500 !important;
  cursor: pointer !important;
}

.breadcrumb-link:hover {
  color: #1f2937 !important;
  background-color: #f3f4f6 !important;
  text-decoration: none !important;
}

.breadcrumb-current {
  color: #1f2937 !important;
  font-weight: 600 !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.375rem !important;
  padding: 0.25rem 0.5rem !important;
}

.breadcrumb-separator {
  color: #9ca3af !important;
  margin: 0 0.5rem !important;
  user-select: none !important;
  font-weight: 400 !important;
}

.breadcrumb-icon {
  width: 1rem !important;
  height: 1rem !important;
  flex-shrink: 0 !important;
}

.breadcrumb-item.active .breadcrumb-current {
  color: #111827 !important;
  font-weight: 600 !important;
}

/* Responsividade */
@media (max-width: 768px) {
  .breadcrumb {
    font-size: 12px !important;
  }
  
  .breadcrumb-link,
  .breadcrumb-current {
    padding: 0.125rem 0.25rem !important;
    font-size: 12px !important;
  }
  
  .breadcrumb-separator {
    margin: 0 0.25rem !important;
  }
}

/* Garantir que os links funcionem corretamente */
.breadcrumb a {
  color: #6b7280 !important;
  text-decoration: none !important;
}

.breadcrumb a:hover {
  color: #1f2937 !important;
  text-decoration: none !important;
}

.breadcrumb a:visited {
  color: #6b7280 !important;
}

.breadcrumb a:active {
  color: #1f2937 !important;
}
</style>